import React, { useState, useEffect, useCallback } from 'react';
import {
  FileDown, Sheet, BarChart2, X, Calendar, TrendingUp,
  TrendingDown, DollarSign, Wallet, Filter, CheckCircle2,
  PieChart as PieIcon, Layers, ArrowUpRight, ArrowDownRight, FileText
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell
} from 'recharts';
import { toast } from 'sonner';
import api from '../utils/api';
import { useTenant } from '../contexts/TenantContext';
import { formatCurrency, formatDate } from '../utils/format';

const TYPE_COLORS = { RECEITA: '#16a34a', CUSTO: '#d97706', DESPESA: '#dc2626' };

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-slate-100 dark:border-slate-700 p-3 text-xs">
      <p className="font-bold text-slate-800 dark:text-slate-100 mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="font-semibold flex items-center gap-1.5" style={{ color: p.color }}>
          <span className="w-2 h-2 rounded-full inline-block" style={{ background: p.color }} />
          {p.name}: {formatCurrency(p.value)}
        </p>
      ))}
    </div>
  );
};

// Só o que já foi pago/recebido entra nos totais (pendentes ficam de fora)
const efetivado = (l) => (l.status === 'pago' || l.status === 'recebido') && !l.ajusteSaldo;

export default function Relatorios() {
  const { tenant } = useTenant();
  const accent = tenant?.corPrimaria || '#0147a0';

  const [lancamentos, setLancamentos] = useState([]);
  const [catData, setCatData]         = useState([]);
  const [loading, setLoading]         = useState(true);
  const [exporting, setExporting]     = useState(null);
  const [filters, setFilters]         = useState({ data_inicio: '', data_fim: '', tipo: '' });
  const [periodPreset, setPeriodPreset] = useState('mes');

  // Helpers para cálculo de datas pré-definidas
  const applyPreset = (preset) => {
    setPeriodPreset(preset);
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();

    if (preset === 'mes') {
      const firstDay = new Date(y, m, 1).toISOString().split('T')[0];
      const lastDay = new Date(y, m + 1, 0).toISOString().split('T')[0];
      setFilters(p => ({ ...p, data_inicio: firstDay, data_fim: lastDay }));
    } else if (preset === 'mes_anterior') {
      const firstDay = new Date(y, m - 1, 1).toISOString().split('T')[0];
      const lastDay = new Date(y, m, 0).toISOString().split('T')[0];
      setFilters(p => ({ ...p, data_inicio: firstDay, data_fim: lastDay }));
    } else if (preset === '30dias') {
      const d = new Date();
      d.setDate(d.getDate() - 30);
      setFilters(p => ({ ...p, data_inicio: d.toISOString().split('T')[0], data_fim: now.toISOString().split('T')[0] }));
    } else if (preset === 'ano') {
      const firstDay = `${y}-01-01`;
      const lastDay = `${y}-12-31`;
      setFilters(p => ({ ...p, data_inicio: firstDay, data_fim: lastDay }));
    } else if (preset === 'tudo') {
      setFilters(p => ({ ...p, data_inicio: '', data_fim: '' }));
    }
  };

  // Inicializa com o mês corrente
  useEffect(() => {
    applyPreset('mes');
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.data_inicio) params.append('data_inicio', filters.data_inicio);
      if (filters.data_fim) params.append('data_fim', filters.data_fim);
      if (filters.tipo) params.append('tipo', filters.tipo);
      const qs = params.toString();

      const [lancRes, catRes] = await Promise.all([
        api.get(`/lancamentos${qs ? '?' + qs : ''}`),
        api.get('/dashboard/faturamento-categoria'),
      ]);
      setLancamentos(lancRes.data || []);

      // Agregar por categoria e tipo
      const byTypeAndCat = {};
      (lancRes.data || []).filter(efetivado).forEach(l => {
        const k = `${l.tipo}||${l.categoria || 'Sem categoria'}`;
        byTypeAndCat[k] = (byTypeAndCat[k] || 0) + (l.valor || 0);
      });
      const agg = Object.entries(byTypeAndCat).map(([k, v]) => {
        const [tipo, categoria] = k.split('||');
        return { tipo, categoria, valor: v };
      }).sort((a, b) => b.valor - a.valor);
      setCatData(agg);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    if (filters.data_inicio !== undefined) {
      fetchData();
    }
  }, [fetchData, filters.data_inicio]);

  const handleExport = async (format) => {
    setExporting(format);
    try {
      const params = new URLSearchParams();
      if (filters.data_inicio) params.append('data_inicio', filters.data_inicio);
      if (filters.data_fim) params.append('data_fim', filters.data_fim);
      if (filters.tipo) params.append('tipo', filters.tipo);
      const endpoint = format;
      const res = await api.get(`/relatorios/export/${endpoint}?${params}`, {
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: res.headers['content-type'] });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ext = format === 'excel' ? 'xlsx' : format === 'pdf' ? 'pdf' : 'csv';
      a.download = `relatorio_financeiro.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(
        format === 'pdf'
          ? 'Relatório PDF executivo baixado!'
          : format === 'csv'
          ? 'Planilha CSV exportada com sucesso!'
          : 'Planilha Excel exportada!'
      );
    } catch (err) {
      console.error('Erro ao exportar:', err);
      toast.error('Erro ao exportar relatório.');
    } finally {
      setExporting(null);
    }
  };

  // Resumo por tipo e quantidades
  const summary = { RECEITA: 0, DESPESA: 0, CUSTO: 0 };
  const counts  = { RECEITA: 0, DESPESA: 0, CUSTO: 0 };
  lancamentos.filter(efetivado).forEach(l => {
    if (summary[l.tipo] !== undefined) {
      summary[l.tipo] += (l.valor || 0);
      counts[l.tipo]  += 1;
    }
  });

  const saldo = summary.RECEITA - summary.CUSTO - summary.DESPESA;
  const margem = summary.RECEITA > 0 ? ((saldo / summary.RECEITA) * 100).toFixed(1) : 0;

  // Chart data
  const chartData = catData.slice(0, 10).map(d => ({
    name: d.categoria.length > 16 ? d.categoria.slice(0, 16) + '...' : d.categoria,
    fullName: d.categoria,
    valor: d.valor,
    tipo: d.tipo,
  }));

  return (
    <div className="w-full max-w-6xl mx-auto space-y-6 page-enter pb-14 px-2 sm:px-4" data-testid="relatorios-page">
      {/* Header Executivo da Página */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-1">
        <div>
          <h1
            className="text-lg sm:text-2xl font-bold tracking-tight text-slate-800 dark:text-slate-100 flex items-center gap-2.5"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            <BarChart2 size={24} style={{ color: accent }} />
            Relatórios & Demonstrativos
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-0.5">
            Análise aprofundada de receitas, despesas, margem líquida e exportação de relatórios executivos.
          </p>
        </div>

        {/* Botões de Ação de Exportação */}
        <div className="flex items-center gap-2.5 flex-shrink-0">
          <button
            onClick={() => handleExport('pdf')}
            disabled={!!exporting}
            className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white text-xs sm:text-sm font-semibold px-4 py-2.5 rounded-xl transition-all active:scale-95 shadow-sm disabled:opacity-50 cursor-pointer"
            data-testid="export-pdf-btn"
            title="Baixar relatório formatado em PDF de alta qualidade"
          >
            {exporting === 'pdf' ? (
              <span className="animate-spin text-sm">⏳</span>
            ) : (
              <FileDown size={16} className="text-rose-400" />
            )}
            <span>Exportar PDF</span>
          </button>

          <button
            onClick={() => handleExport('excel')}
            disabled={!!exporting}
            className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs sm:text-sm font-semibold px-4 py-2.5 rounded-xl transition-all active:scale-95 shadow-sm disabled:opacity-50 cursor-pointer"
            data-testid="export-excel-btn"
            title="Baixar dados tabulares em planilha Excel (XLSX)"
          >
            {exporting === 'excel' ? (
              <span className="animate-spin text-sm">⏳</span>
            ) : (
              <Sheet size={16} className="text-white" />
            )}
            <span>Exportar Excel</span>
          </button>

          <button
            onClick={() => handleExport('csv')}
            disabled={!!exporting}
            className="inline-flex items-center gap-2 bg-slate-700 hover:bg-slate-800 text-white text-xs sm:text-sm font-semibold px-4 py-2.5 rounded-xl transition-all active:scale-95 shadow-sm disabled:opacity-50 cursor-pointer"
            data-testid="export-csv-btn"
            title="Baixar dados tabulares em CSV universal"
          >
            {exporting === 'csv' ? (
              <span className="animate-spin text-sm">⏳</span>
            ) : (
              <FileText size={16} className="text-white" />
            )}
            <span>Exportar CSV</span>
          </button>
        </div>
      </div>

      {/* Barra de Filtros e Atalhos Rápidos de Período */}
      <div className="fin-card p-4 sm:p-5 border border-slate-200/80 dark:border-slate-800 rounded-2xl space-y-4">
        {/* Quick Period Pills */}
        <div className="flex flex-wrap items-center justify-between gap-2 pb-3 border-b border-slate-100 dark:border-slate-800">
          <div className="flex items-center gap-1.5 text-xs font-bold text-slate-500 uppercase tracking-wider">
            <Calendar size={14} className="text-slate-400" />
            <span>Período Rápido:</span>
          </div>

          <div className="flex flex-wrap items-center gap-1.5">
            {[
              { id: 'mes',          label: 'Este Mês' },
              { id: 'mes_anterior', label: 'Mês Anterior' },
              { id: '30dias',       label: 'Últimos 30 Dias' },
              { id: 'ano',          label: 'Este Ano' },
              { id: 'tudo',         label: 'Todo o Histórico' },
            ].map(p => (
              <button
                key={p.id}
                onClick={() => applyPreset(p.id)}
                className={`text-xs font-semibold px-3 py-1.5 rounded-xl transition-all cursor-pointer ${
                  periodPreset === p.id
                    ? 'text-white shadow-sm'
                    : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
                }`}
                style={periodPreset === p.id ? { background: accent } : {}}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* Inputs de Data e Tipo */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
          <div>
            <label className="fin-label text-xs">Data de Início</label>
            <input
              type="date"
              value={filters.data_inicio}
              onChange={e => {
                setPeriodPreset('custom');
                setFilters(p => ({ ...p, data_inicio: e.target.value }));
              }}
              className="fin-input text-xs"
              data-testid="relatorio-data-inicio"
            />
          </div>

          <div>
            <label className="fin-label text-xs">Data Final</label>
            <input
              type="date"
              value={filters.data_fim}
              onChange={e => {
                setPeriodPreset('custom');
                setFilters(p => ({ ...p, data_fim: e.target.value }));
              }}
              className="fin-input text-xs"
              data-testid="relatorio-data-fim"
            />
          </div>

          <div>
            <label className="fin-label text-xs">Tipo de Lançamento</label>
            <div className="flex gap-2 items-center">
              <select
                value={filters.tipo}
                onChange={e => setFilters(p => ({ ...p, tipo: e.target.value }))}
                className="fin-input flex-1 text-xs"
                data-testid="relatorio-tipo"
              >
                <option value="">Todos os tipos</option>
                <option value="RECEITA">Somente Receitas</option>
                <option value="DESPESA">Somente Despesas</option>
                <option value="CUSTO">Somente Custos</option>
              </select>

              {(filters.data_inicio || filters.data_fim || filters.tipo) && (
                <button
                  onClick={() => {
                    setPeriodPreset('tudo');
                    setFilters({ data_inicio: '', data_fim: '', tipo: '' });
                  }}
                  className="p-2 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-400 hover:text-red-500 hover:border-red-200 transition-colors cursor-pointer"
                  title="Limpar todos os filtros"
                  data-testid="clear-relatorio-filters-btn"
                >
                  <X size={15} />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Cards de Indicadores Financeiros (KPIs Executivos) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="relatorio-summary">
        {/* Receitas */}
        <div className="fin-card p-4 sm:p-5 border border-slate-200/80 dark:border-slate-800 rounded-2xl bg-white dark:bg-slate-800/90 flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Total Receitas</span>
            <div className="w-8 h-8 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 flex items-center justify-center">
              <TrendingUp size={16} />
            </div>
          </div>
          <div className="mt-3">
            <span className="text-xl sm:text-2xl font-extrabold text-emerald-600 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {formatCurrency(summary.RECEITA)}
            </span>
            <p className="text-[11px] text-slate-400 mt-0.5">
              {counts.RECEITA} lançamento{counts.RECEITA !== 1 ? 's' : ''} computado{counts.RECEITA !== 1 ? 's' : ''}
            </p>
          </div>
        </div>

        {/* Custos */}
        <div className="fin-card p-4 sm:p-5 border border-slate-200/80 dark:border-slate-800 rounded-2xl bg-white dark:bg-slate-800/90 flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Custos Operacionais</span>
            <div className="w-8 h-8 rounded-xl bg-amber-50 dark:bg-amber-950/40 text-amber-600 flex items-center justify-center">
              <Layers size={16} />
            </div>
          </div>
          <div className="mt-3">
            <span className="text-xl sm:text-2xl font-extrabold text-amber-600 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {formatCurrency(summary.CUSTO)}
            </span>
            <p className="text-[11px] text-slate-400 mt-0.5">
              {counts.CUSTO} lançamento{counts.CUSTO !== 1 ? 's' : ''} computado{counts.CUSTO !== 1 ? 's' : ''}
            </p>
          </div>
        </div>

        {/* Despesas */}
        <div className="fin-card p-4 sm:p-5 border border-slate-200/80 dark:border-slate-800 rounded-2xl bg-white dark:bg-slate-800/90 flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Total Despesas</span>
            <div className="w-8 h-8 rounded-xl bg-rose-50 dark:bg-rose-950/40 text-rose-600 flex items-center justify-center">
              <TrendingDown size={16} />
            </div>
          </div>
          <div className="mt-3">
            <span className="text-xl sm:text-2xl font-extrabold text-rose-600 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {formatCurrency(summary.DESPESA)}
            </span>
            <p className="text-[11px] text-slate-400 mt-0.5">
              {counts.DESPESA} lançamento{counts.DESPESA !== 1 ? 's' : ''} computado{counts.DESPESA !== 1 ? 's' : ''}
            </p>
          </div>
        </div>

        {/* Resultado Líquido */}
        <div className="fin-card p-4 sm:p-5 border border-slate-200/80 dark:border-slate-800 rounded-2xl bg-white dark:bg-slate-800/90 flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Resultado Líquido</span>
            <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${
              saldo >= 0 ? 'bg-emerald-50 text-emerald-600 dark:bg-emerald-950/40' : 'bg-rose-50 text-rose-600 dark:bg-rose-950/40'
            }`}>
              <Wallet size={16} />
            </div>
          </div>
          <div className="mt-3">
            <span className={`text-xl sm:text-2xl font-extrabold tracking-tight ${saldo >= 0 ? 'text-emerald-600' : 'text-rose-600'}`} style={{ fontFamily: 'Manrope, sans-serif' }}>
              {formatCurrency(saldo)}
            </span>
            <p className="text-[11px] text-slate-400 mt-0.5 flex items-center gap-1">
              <span>Margem Líquida:</span>
              <span className={`font-bold ${saldo >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{margem}%</span>
            </p>
          </div>
        </div>
      </div>

      {/* Gráfico de Distribuição por Categoria */}
      <div className="fin-card p-5 sm:p-6 border border-slate-200/80 dark:border-slate-800 rounded-2xl" data-testid="relatorio-chart">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-bold text-slate-800 dark:text-slate-100 text-base" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Top 10 Categorias por Volume Financeiro
            </h2>
            <p className="text-xs text-slate-400">Distribuição comparativa de receitas, despesas e custos</p>
          </div>
        </div>

        {chartData.length === 0 ? (
          <div className="h-[240px] flex items-center justify-center text-slate-400 text-sm">
            Nenhum dado encontrado para o período selecionado.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 50 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#94A3B8' }} angle={-25} textAnchor="end" interval={0} />
              <YAxis tick={{ fontSize: 10, fill: '#94A3B8' }} tickFormatter={v => `R$${v >= 1000 ? `${(v/1000).toFixed(0)}k` : v}`} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="valor" name="Valor" radius={[6, 6, 0, 0]} maxBarSize={48}>
                {chartData.map((d, i) => (
                  <Cell key={i} fill={TYPE_COLORS[d.tipo] || accent} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Tabela de Resumo por Categoria */}
      <div className="fin-card border border-slate-200/80 dark:border-slate-800 rounded-2xl overflow-hidden" data-testid="relatorio-table">
        <div className="px-5 sm:px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
          <div>
            <h2 className="font-bold text-slate-800 dark:text-slate-100 text-base" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Detalhamento por Categoria
            </h2>
            <p className="text-xs text-slate-400">Total acumulado e representatividade percentual</p>
          </div>
          <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
            {catData.length} categoria{catData.length !== 1 ? 's' : ''}
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50/80 dark:bg-slate-800/50 border-b border-slate-100 dark:border-slate-800">
                <th className="text-left px-3 sm:px-5 py-3 text-[11px] font-bold uppercase tracking-wider text-slate-400">Tipo</th>
                <th className="text-left px-3 sm:px-5 py-3 text-[11px] font-bold uppercase tracking-wider text-slate-400">Categoria</th>
                <th className="text-right px-3 sm:px-5 py-3 text-[11px] font-bold uppercase tracking-wider text-slate-400">Total</th>
                <th className="text-left px-3 sm:px-5 py-3 text-[11px] font-bold uppercase tracking-wider text-slate-400 w-28 sm:w-44 hidden sm:table-cell">Participação</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {loading ? (
                Array(5).fill(0).map((_, i) => (
                  <tr key={i}>
                    {Array(4).fill(0).map((_, j) => (
                      <td key={j} className="px-3 sm:px-5 py-3"><div className="h-3.5 bg-slate-100 dark:bg-slate-700 rounded animate-pulse" /></td>
                    ))}
                  </tr>
                ))
              ) : catData.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-3 sm:px-5 py-10 text-center text-slate-400 text-sm">
                    Nenhum lançamento encontrado para o período informado.
                  </td>
                </tr>
              ) : (
                catData.map((d, i) => {
                  const total = catData.reduce((s, r) => s + r.valor, 0);
                  const pct = total > 0 ? ((d.valor / total) * 100).toFixed(1) : '0.0';
                  return (
                    <tr key={i} className="hover:bg-slate-50/60 dark:hover:bg-slate-800/40 transition-colors">
                      <td className="px-3 sm:px-5 py-3">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold uppercase ${
                            d.tipo === 'RECEITA' ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400' :
                            d.tipo === 'CUSTO' ? 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400' :
                            'bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-400'
                          }`}
                        >
                          {d.tipo}
                        </span>
                      </td>
                      <td className="px-3 sm:px-5 py-3 font-medium text-slate-700 dark:text-slate-200">
                        {d.categoria}
                      </td>
                      <td className="px-3 sm:px-5 py-3 text-right font-bold" style={{ color: TYPE_COLORS[d.tipo] }}>
                        {formatCurrency(d.valor)}
                      </td>
                      <td className="px-3 sm:px-5 py-3 hidden sm:table-cell">
                        <div className="flex items-center gap-2.5">
                          <div className="flex-1 bg-slate-100 dark:bg-slate-700 rounded-full h-2 overflow-hidden">
                            <div
                              className="h-full rounded-full transition-all duration-500"
                              style={{ width: `${Math.min(pct, 100)}%`, background: TYPE_COLORS[d.tipo] }}
                            />
                          </div>
                          <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 w-10 text-right">{pct}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
